import { useCallback, useEffect, useState } from 'react';
import { KeyIcon, PlusIcon } from '@heroicons/react/24/outline';
import { foldAccessAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function errorMessage(error) {
  return error.response?.data?.message || error.response?.data?.error || error.message || 'Something went wrong.';
}

export default function FoldAccessPage() {
  const { admin, isOwner } = useAuth();
  const [access, setAccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');

  const loadAccess = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await foldAccessAPI.list();
      setAccess(response.data.access || []);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) loadAccess();
  }, [isOwner, loadAccess]);

  if (!isOwner) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
        Fold owner access is required to manage who can enter The Fold.
      </div>
    );
  }

  const grant = async (event) => {
    event.preventDefault();
    setError('');
    setSavingId('new');
    try {
      await foldAccessAPI.grant(email.trim(), role);
      setEmail('');
      setRole('admin');
      await loadAccess();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSavingId(null);
    }
  };

  const update = async (entry, updates) => {
    const disabling = updates.status === 'disabled';
    if (disabling && !window.confirm(`Disable Fold access for ${entry.email}?`)) return;

    setError('');
    setSavingId(entry.access_id);
    try {
      await foldAccessAPI.update(entry.access_id, updates);
      await loadAccess();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-deep-navy p-2 text-white"><KeyIcon className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-deep-navy">Fold Access</h1>
            <p className="text-sm text-storm-blue">Control which Google-linked Flock accounts can enter this environment.</p>
          </div>
        </div>
      </header>

      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <form onSubmit={grant} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="font-semibold text-deep-navy">Grant access</h2>
        <p className="mt-1 text-sm text-gray-500">The email must already belong to an active Google-linked Flock user.</p>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_160px_auto]">
          <label>
            <span className="sr-only">Email address</span>
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="person@example.com"
              className="min-h-[44px] w-full rounded-xl border border-gray-300 px-3 focus:border-storm-blue focus:outline-none focus:ring-2 focus:ring-storm-blue/20"
            />
          </label>
          <label>
            <span className="sr-only">Fold role</span>
            <select value={role} onChange={(event) => setRole(event.target.value)} className="min-h-[44px] w-full rounded-xl border border-gray-300 px-3">
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </label>
          <button type="submit" disabled={savingId === 'new'} className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-deep-navy px-5 font-medium text-white disabled:opacity-50">
            <PlusIcon className="h-5 w-5" /> Grant
          </button>
        </div>
      </form>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-4">
          <h2 className="font-semibold text-deep-navy">Approved accounts</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-storm-blue">Loading access…</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {access.map((entry) => {
              const isSelf = entry.user_id === admin.id;
              const saving = savingId === entry.access_id;
              return (
                <div key={entry.access_id} className="grid gap-4 p-5 md:grid-cols-[1fr_150px_150px] md:items-center">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-deep-navy">{entry.name || entry.email}</div>
                    <div className="truncate text-sm text-gray-500">{entry.email}{isSelf ? ' · You' : ''}</div>
                  </div>
                  <select
                    aria-label={`Role for ${entry.email}`}
                    value={entry.role}
                    disabled={saving || isSelf}
                    onChange={(event) => update(entry, { role: event.target.value })}
                    className="min-h-[44px] rounded-xl border border-gray-300 px-3 disabled:bg-gray-100"
                  >
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                  <button
                    type="button"
                    disabled={saving || isSelf}
                    onClick={() => update(entry, { status: entry.status === 'active' ? 'disabled' : 'active' })}
                    className={`min-h-[44px] rounded-xl border px-4 font-medium disabled:cursor-not-allowed disabled:opacity-50 ${entry.status === 'active' ? 'border-red-200 text-red-700 hover:bg-red-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}
                  >
                    {entry.status === 'active' ? 'Disable' : 'Reactivate'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
